// Composite search router.
//
// PersonDB rejects employer-firmographic filters (revenue, funding,
// headcount growth, year founded, crunchbase categories — the
// autocomplete probe at cleanse-api-v2/drafts/autocomplete_probe.json
// confirms every one returns 400). The composite endpoint
// /api/v1/people-search/by-companies bridges the gap: it resolves
// firmographics → company URLs via CompanyDB, then runs PersonDB
// scoped to that set.
//
// This module decides — purely from the SearchFilters / CompanySearchFilters
// state — whether the search should:
//   (a) hit /people-search/search (plain — no firmographic filters)
//   (b) hit /people-search/by-companies (composite — at least one
//       company-side filter is active)
//
// Used by the people-search-hook to swap mutations transparently.

import { hasStartupPrograms } from '@/containers/search/startup-programs/startup-programs.utils';
import type { SearchFilters } from '@/types/search-filters';
import {
  type CompanySearchFilters,
  activeCompanyFilterKeys,
  buildCompanyFilters,
  INITIAL_COMPANY_FILTERS,
} from '@/utils/company-search.utils';

export interface CompositeRouteDecision {
  /** When true the caller should use the composite endpoint. */
  useComposite: boolean;
  /**
   * Reason composite was triggered. Today: dev console only via the
   * search hook's diagnostic log. Future: feed into PostHog
   * ``search_routing_decision`` once telemetry lands. Also a
   * convenient surface for a future results-panel banner that
   * explains why the user's company-side filters caused a different
   * code path.
   */
  reason: string;
  /**
   * The CompanyDB filter blob to send as ``company_filter`` on the
   * composite request. ``null`` when composite isn't needed.
   */
  companyFilter: Record<string, unknown> | null;
}

/**
 * Decide whether the People search needs the composite endpoint. The
 * company concepts PersonDB can answer (industry, headcount band, HQ
 * location, company name, domain, LinkedIn URL) live on the People keys and
 * never reach this slice from the panel, so any key set here routes
 * composite.
 *
 * With no company key set the plain endpoint runs, which is cheaper.
 */
export function detectCompositeRoute(
  peopleFilters: SearchFilters,
  companyFilters: CompanySearchFilters
): CompositeRouteDecision {
  if (hasStartupPrograms(companyFilters.startupPrograms)) {
    return {
      useComposite: true,
      reason: 'startup program filter',
      companyFilter: normalizeBuiltFilters(buildCompanyFilters(companyFilters)),
    };
  }
  // Explicit override — the user can flip the SearchFilters flag to
  // force composite mode (e.g. for "Find contacts at these saved
  // companies" flows where the company set is pre-resolved). But only
  // when at least one company-side filter is actually set: an empty
  // companyFilter would hit the composite endpoint with ``{}`` and
  // resolve against the entire CompanyDB universe before person-
  // scoping — orders-of-magnitude slower (and historically 4xx/5xx on
  // some Crustdata tiers). Round-2 reliability audit S2.
  if (peopleFilters.companyFilterCompositeMode) {
    const built = normalizeBuiltFilters(buildCompanyFilters(companyFilters));
    if (built) {
      return {
        useComposite: true,
        reason: 'composite mode toggled on',
        companyFilter: built,
      };
    }
    // Override set but no actual company filter — fall through to the
    // plain endpoint and surface the reason so the People hook can
    // optionally log/telemetry it.
    return {
      useComposite: false,
      reason: 'composite mode set but no company filter — downgraded',
      companyFilter: null,
    };
  }

  // Any company key set in the composite slice routes composite. The People
  // panel writes the slice only for concepts PersonDB cannot answer
  // (revenue, funding, NAICS, company type, team make-up...); industry, size,
  // HQ and company identity go on the People keys. So a key in the slice is
  // either one PersonDB cannot satisfy, or one a saved search or Clu put
  // there, and both have to reach the search. The key list is the complete
  // one from ``activeCompanyFilterKeys``: the old hand-kept trigger list
  // missed companyType, workforceRules, headcountRanges, industries and HQ
  // state / city, and the search silently ran without them.
  const triggered = activeCompanyFilterKeys(companyFilters);
  if (triggered.length === 0) {
    return {
      useComposite: false,
      reason: 'no company filter',
      companyFilter: null,
    };
  }

  // A slice holding only unusable values (``headcountRanges: ['bogus']``,
  // ``hqStates: ['']``) counts as set but compiles to nothing. Sent combined
  // with ``{}``, the company half is refused (400 "At least one of 'filters'
  // or 'search' is required"); run the plain search, as staging did.
  const companyFilter = normalizeBuiltFilters(
    buildCompanyFilters(companyFilters)
  );
  if (!companyFilter) {
    return {
      useComposite: false,
      reason: 'company filter compiled to nothing',
      companyFilter: null,
    };
  }

  return {
    useComposite: true,
    reason: `composite filters active: ${triggered.slice(0, 3).join(', ')}${
      triggered.length > 3 ? '…' : ''
    }`,
    companyFilter,
  };
}

/**
 * ``buildCompanyFilters`` returns ``{}`` when no condition was emitted.
 * Treat empty objects as null so callers downstream don't send an
 * empty filter group to the composite endpoint.
 */
function normalizeBuiltFilters(
  built: Record<string, unknown>
): Record<string, unknown> | null {
  if (!built || Object.keys(built).length === 0) {
    return null;
  }
  return built;
}

/**
 * Helper: render a CompanySearchFilters snapshot equivalent to
 * INITIAL_COMPANY_FILTERS plus the keys that come back from a saved
 * search blob. Used when the people-search hook needs a "no company
 * filter" sentinel without importing the constant from a circular dep.
 */
export function emptyCompanyFilters(): CompanySearchFilters {
  return { ...INITIAL_COMPANY_FILTERS };
}
